import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** ms; 0 keeps it until dismissed. Errors default to 8s, everything else 5s. */
  duration?: number;
  action?: ToastAction;
  /** Reuse an id to replace an existing toast instead of stacking a new one. */
  id?: string;
}

interface ToastRecord extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  /** Show a toast; returns its id so it can be replaced or dismissed. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
} as const;

const TONES: Record<ToastTone, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-danger',
};

const DEFAULT_DURATION: Record<ToastTone, number> = {
  info: 5000,
  success: 4500,
  warning: 7000,
  error: 8000,
};

/** At most this many are on screen; older ones are dropped from the top. */
const MAX_VISIBLE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((current) => current.filter((item) => item.id !== id));
    },
    [clearTimer],
  );

  const dismissAll = useCallback(() => {
    for (const id of timers.current.keys()) window.clearTimeout(timers.current.get(id));
    timers.current.clear();
    setToasts([]);
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const tone = options.tone ?? 'info';
      const id = options.id ?? crypto.randomUUID();
      const record: ToastRecord = { ...options, tone, id };

      setToasts((current) => {
        const withoutDuplicate = current.filter((item) => item.id !== id);
        return [...withoutDuplicate, record].slice(-MAX_VISIBLE);
      });

      clearTimer(id);
      const duration = options.duration ?? DEFAULT_DURATION[tone];
      if (duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [clearTimer, dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ toast, dismiss, dismissAll }), [toast, dismiss, dismissAll]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions text"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-end gap-2 sm:left-auto sm:right-6 sm:w-full sm:max-w-sm"
      >
        <AnimatePresence initial={false}>
          {toasts.map((item) => {
            const tone = item.tone ?? 'info';
            const Icon = ICONS[tone];
            return (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.14 } }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="pap-pop pointer-events-auto flex w-full items-start gap-3 px-4 py-3"
                onMouseEnter={() => clearTimer(item.id)}
              >
                <Icon className={cn('mt-0.5 size-4 shrink-0', TONES[tone])} aria-hidden />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium text-fg">{item.title}</p>
                  {item.description && (
                    <p className="text-xs leading-relaxed text-muted">{item.description}</p>
                  )}
                  {item.action && (
                    <button
                      type="button"
                      onClick={() => {
                        item.action?.onClick();
                        dismiss(item.id);
                      }}
                      className="pt-0.5 text-xs font-medium text-accent underline-offset-4 hover:underline"
                    >
                      {item.action.label}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Dismiss notification"
                  onClick={() => dismiss(item.id)}
                  className="-mr-1 shrink-0 rounded p-1 text-subtle transition-colors hover:bg-surface hover:text-fg"
                >
                  <X className="size-3.5" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

/** `const { toast } = useToast(); toast({ title: 'Published', tone: 'success' })` */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
